import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Icons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NAV_ITEMS } from '@/lib/navigation-config';
import type { NavigationItem } from '@/types/navigation';
import { useCurrentOrganization } from '@/hooks/use-organization';
import { useInboxSummary } from '@/features/inbox/hooks/useInboxSummary';
import { formatInboxBadgeCount } from '@/features/inbox/inboxBadgeCount';

interface SidebarNavProps {
  isCollapsed?: boolean;
  onItemClick?: () => void;
  items?: NavigationItem[];
}

function pathMatches(pathname: string, itemPath: string): boolean {
  if (itemPath === '/') {
    return pathname === '/';
  }
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

function isNavItemHighlighted(item: NavigationItem, pathname: string): boolean {
  if (item.id === 'govern') {
    return pathname.startsWith('/govern');
  }
  if (item.id === 'settings') {
    return pathname.startsWith('/settings');
  }
  if (pathMatches(pathname, item.path)) {
    return true;
  }
  return hasActiveDescendant(item, pathname);
}

function hasActiveDescendant(item: NavigationItem, pathname: string): boolean {
  if (!item.children?.length) {
    return false;
  }
  return item.children.some(
    (c) =>
      pathMatches(pathname, c.path) ||
      (c.children?.length ? hasActiveDescendant(c, pathname) : false)
  );
}

interface RowClassesOpts {
  isHighlighted: boolean;
  disabled?: boolean;
  isCollapsed?: boolean;
  isChild?: boolean;
}

function rowClassName({ isHighlighted, disabled, isCollapsed, isChild }: RowClassesOpts): string {
  return cn(
    'group flex w-full items-center rounded-md text-sm font-medium transition-colors',
    isChild ? 'px-2 py-1.5' : 'px-3 py-2',
    'hover:bg-accent hover:text-accent-foreground',
    isHighlighted ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
    disabled && 'opacity-50 cursor-not-allowed',
    isCollapsed ? 'justify-center px-2' : 'gap-2.5'
  );
}

interface NavRenderOpts {
  isCollapsed?: boolean;
  onItemClick?: () => void;
  pathname: string;
  t: (key: string) => string;
  iconMap: Record<string, LucideIcon>;
  isChild?: boolean;
  showParentLabel?: boolean;
  /** Inbox items that arrived since this member last opened the inbox. */
  inboxNewCount?: number;
  /** Pre-interpolated, because the renderer's `t` is the untyped narrow form. */
  inboxBadgeAriaLabel?: string;
}

function renderNavLink(item: NavigationItem, opts: NavRenderOpts) {
  const Icon = opts.iconMap[item.icon];
  const highlighted = isNavItemHighlighted(item, opts.pathname) && !item.disabled;
  const badgeCount = item.id === 'inbox' ? (opts.inboxNewCount ?? 0) : 0;
  const badgeLabel = formatInboxBadgeCount(badgeCount);

  return (
    <NavLink
      key={item.id}
      to={item.disabled ? '#' : item.path}
      end={item.path === '/'}
      title={opts.t(item.label)}
      aria-label={
        badgeCount > 0 ? (opts.inboxBadgeAriaLabel ?? opts.t(item.label)) : opts.t(item.label)
      }
      onClick={(e) => {
        if (item.disabled) {
          e.preventDefault();
          return;
        }
        opts.onItemClick?.();
      }}
      className={() =>
        rowClassName({
          isHighlighted: highlighted,
          disabled: Boolean(item.disabled),
          ...(opts.isCollapsed !== undefined ? { isCollapsed: opts.isCollapsed } : {}),
          ...(opts.isChild !== undefined ? { isChild: opts.isChild } : {}),
        })
      }
    >
      {Icon && (
        <span className="relative flex shrink-0 items-center">
          <Icon
            className={cn(
              opts.isChild ? 'h-4 w-4' : 'h-5 w-5',
              'shrink-0 transition-colors',
              !item.disabled && 'group-hover:text-foreground'
            )}
          />
          {/* Collapsed shows only the icon, so the count degrades to a dot. */}
          {badgeCount > 0 && opts.isCollapsed && (
            <span
              className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary ring-2 ring-background"
              aria-hidden
            />
          )}
        </span>
      )}
      {!opts.isCollapsed && (
        <span className={cn(opts.showParentLabel && item.children?.length && 'font-semibold')}>
          {opts.t(item.label)}
        </span>
      )}
      {badgeCount > 0 && !opts.isCollapsed && (
        <span className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-xs font-semibold leading-none text-primary-foreground">
          {badgeLabel}
        </span>
      )}
    </NavLink>
  );
}

function NavItemTree({
  item,
  opts,
  depth = 0,
}: {
  item: NavigationItem;
  opts: NavRenderOpts;
  depth?: number;
}) {
  const sectionActive = isNavItemHighlighted(item, opts.pathname);
  const [expanded, setExpanded] = useState(() => sectionActive && Boolean(item.collapsible));

  useEffect(() => {
    if (sectionActive && item.collapsible) {
      setExpanded(true);
    }
  }, [sectionActive, item.collapsible]);

  const showChildren =
    Boolean(item.children?.length) && !opts.isCollapsed && (!item.collapsible || expanded);

  const childIndentClass =
    depth > 0
      ? 'ml-2.5 flex flex-col gap-0.5 border-l border-border pl-2.5'
      : 'mb-1 ml-2.5 flex flex-col gap-0.5 border-l border-border pl-2.5';

  if (item.collapsible && item.children?.length && !opts.isCollapsed) {
    const Icon = opts.iconMap[item.icon];
    const highlighted = sectionActive && !item.disabled;

    return (
      <div className="flex flex-col gap-0.5">
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={`sidebar-section-${item.id}`}
          title={opts.t(item.label)}
          onClick={() => setExpanded((open) => !open)}
          className={rowClassName({
            isHighlighted: highlighted,
            disabled: Boolean(item.disabled),
          })}
        >
          {Icon ? (
            <Icon
              className={cn(
                'h-5 w-5 shrink-0 transition-colors',
                !item.disabled && 'group-hover:text-foreground'
              )}
            />
          ) : null}
          <span className="min-w-0 flex-1 text-left font-semibold">{opts.t(item.label)}</span>
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
          )}
        </button>

        {showChildren ? (
          <div id={`sidebar-section-${item.id}`} className={childIndentClass}>
            {item.children.map((child) =>
              child.children?.length ? (
                <NavItemTree key={child.id} item={child} opts={opts} depth={depth + 1} />
              ) : (
                renderNavLink(child, { ...opts, isChild: true, showParentLabel: false })
              )
            )}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {renderNavLink(item, { ...opts, showParentLabel: true, isChild: depth > 0 })}

      {showChildren ? (
        <div className={childIndentClass}>
          {item.children!.map((child) =>
            child.children?.length ? (
              <NavItemTree key={child.id} item={child} opts={opts} depth={depth + 1} />
            ) : (
              renderNavLink(child, { ...opts, isChild: true, showParentLabel: false })
            )
          )}
        </div>
      ) : null}
    </div>
  );
}

export function SidebarNav({ isCollapsed, onItemClick, items }: SidebarNavProps) {
  const { t } = useTranslation('layout');
  const { pathname } = useLocation();
  const iconMap = Icons as unknown as Record<string, LucideIcon>;
  const navItems = items ?? NAV_ITEMS;

  // Read here rather than threading a count down from DashboardLayout. This
  // renders in both the desktop rail and the mobile drawer; TanStack dedupes
  // the two on the shared query key, so it is one request either way.
  const { organization } = useCurrentOrganization();
  const { data: inboxSummary } = useInboxSummary(organization?.organizationId);
  const inboxNewCount = inboxSummary?.new_count ?? 0;

  const navOpts: NavRenderOpts = {
    pathname,
    t,
    iconMap,
    ...(inboxNewCount > 0
      ? {
          inboxNewCount,
          inboxBadgeAriaLabel: t('nav.inboxNewCount', {
            count: inboxNewCount,
            label: t('nav.inbox'),
          }),
        }
      : {}),
    ...(isCollapsed !== undefined ? { isCollapsed } : {}),
    ...(onItemClick !== undefined ? { onItemClick } : {}),
  };

  return (
    <nav className="min-h-0 flex-1 overflow-y-auto">
      <div className="flex flex-col gap-1 px-2 py-4">
        {navItems.map((item) => {
          if (isCollapsed) {
            return renderNavLink(item, navOpts);
          }
          return <NavItemTree key={item.id} item={item} opts={navOpts} />;
        })}
      </div>
    </nav>
  );
}
